import {
  ComposedChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import ChartCard, { yearTickFactory } from './ChartCard';
import ChartTooltip from './ChartTooltip';
import { tooltipNotesForYear } from './chartNotes';
import MarketDataFootnote from './MarketDataFootnote';
import TrendInsightsFooter from './TrendInsightsFooter';
import { gridProps, legendProps, PEER_COLORS, FOCAL_COLOR, CHART_INK, CHART_TYPE } from './chartTheme';
import { formatFiscalYear, formatCurrencyValue } from '../utils/formatters';
import type { UnifiedFinancialRecord, RegionConfig, DataNote } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
}

export default function EVTrendChart({ data, region }: Props) {
  const chartData = data.map((d) => {
    const ev = d.enterprise_value ?? null;
    const marketCap = d.market_cap ?? null;
    // Show the raw value — a genuine 0 at FY21 should plot at 0, not be
    // skipped. Only treat null/undefined as missing.
    const evEbitda = d.ev_ebitda ?? null;
    // A year is "empty" only when NONE of the three series has a value; such a
    // year still stays on the axis, flagged, with a tooltip explaining the gap.
    const hasValue = ev != null || marketCap != null || evEbitda != null;
    return {
      year: formatFiscalYear(d.year, region),
      ev,
      marketCap,
      evEbitda,
      // Carried through so the tick/tooltip can explain adjusted OR empty years.
      notes: tooltipNotesForYear(d.data_notes, hasValue),
    };
  });

  // Full-height invisible hover target for EVERY year. Recharts' <Tooltip> only
  // fires where a series has a point to latch onto, so an empty year (a gap in
  // all three lines, e.g. a pre-listing year) is otherwise un-hoverable and its
  // "not calculated" note never shows. A transparent bar spanning each column
  // gives the tooltip something to activate on across the whole plot width.
  const yMax = Math.max(0, ...chartData.map((d) => Math.max(d.ev ?? 0, d.marketCap ?? 0)));
  const plotData = chartData.map((d) => ({ ...d, hoverSpan: yMax > 0 ? yMax : null }));

  const notesByYear = new Map<string, DataNote[]>(chartData.map((d) => [d.year, d.notes]));

  return (
    <ChartCard
      title={`EV/EBITDA Trend (${region?.currency?.unit_abbr})`}
      subtitle="Historical EV/EBITDA multiple with CAGR analysis"
    >
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={plotData} margin={{ top: 20, right: 40, left: 8, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="year" tick={yearTickFactory(notesByYear)} />
          <YAxis
            yAxisId="left"
            width={80}
            tick={{ fontSize: CHART_TYPE.axisTick, fill: CHART_INK.label }}
            tickFormatter={(v) => formatCurrencyValue(v, region, true)}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: CHART_TYPE.axisTick, fill: CHART_INK.label }}
            tickFormatter={(v) => `${v?.toFixed(1)}x`}
          />
          <Tooltip
            content={
              <ChartTooltip
                notesKey="notes"
                format={(v, name) =>
                  name === '__hover'
                    ? null
                    : name === 'EV/EBITDA'
                      ? `${v.toFixed(1)}x`
                      : formatCurrencyValue(v, region)
                }
              />
            }
          />
          <Legend {...legendProps} />
          {/* Invisible full-height hover target so EVERY year (incl. empty ones)
              activates the tooltip. Rendered first = behind the real series;
              hidden from the legend and suppressed as a tooltip row. */}
          <Bar
            yAxisId="left"
            dataKey="hoverSpan"
            name="__hover"
            fill="transparent"
            fillOpacity={0}
            stroke="none"
            legendType="none"
            isAnimationActive={false}
            barSize={48}
          />
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="ev"
            name="EV"
            fill={PEER_COLORS[0]}
            fillOpacity={0.15}
            stroke={PEER_COLORS[0]}
            strokeWidth={2}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="marketCap"
            name="Market Cap"
            stroke={PEER_COLORS[2]}
            dot={{ r: 3 }}
            strokeWidth={2}
            strokeDasharray="4 2"
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="evEbitda"
            name="EV/EBITDA"
            stroke={FOCAL_COLOR}
            dot={{ r: 3 }}
            strokeWidth={2}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <MarketDataFootnote
        records={data}
        region={region}
        hasMetric={(r) =>
          r.enterprise_value != null || r.market_cap != null || r.ev_ebitda != null
        }
        metricLabel="EV, market cap and the EV/EBITDA multiple"
      />
      <TrendInsightsFooter values={data.map((d) => d.enterprise_value ?? null)} metricLabel="EV" />
    </ChartCard>
  );
}
